import { supabase } from '@/lib/supabase';
import type { ChecklistTriggerPayload } from '@/lib/checklistTriggerPayload';

const API_URL = import.meta.env.VITE_PYTHON_BACKEND_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';

interface FieldTriggerPayload {
  project_id: string;
  field_id: string;
  field_name: string;
  old_value: any;
  new_value: any;
}

/**
 * Service for triggering field completion automations
 */
class FieldTriggerService {
  /**
   * Check if a project field update should trigger automations
   * This is called asynchronously and doesn't block the UI
   *
   * @param payload Field update information
   */
  async checkFieldTriggers(payload: FieldTriggerPayload): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('No active session for field trigger check');
        return;
      }

      const response = await fetch(`${API_URL}/api/v1/automation/check-field-triggers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Field trigger check failed:', error);
      } else {
        const result = await response.json();
      }
    } catch (error) {
      console.error('Error checking field triggers:', error);
      // Don't throw error - field triggers are non-blocking
    }
  }

  /**
   * Check if a checklist item update should trigger automations
   * This is called asynchronously and doesn't block the UI
   *
   * @param payload Checklist item update information
   */
  async checkChecklistTriggers(payload: ChecklistTriggerPayload): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('No active session for checklist trigger check');
        return;
      }

      const response = await fetch(`${API_URL}/api/v1/automation/check-checklist-triggers/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Checklist trigger check failed:', error);
      } else {
        const result = await response.json();
      }
    } catch (error) {
      console.error('Error checking checklist triggers:', error);
      // Don't throw error - checklist triggers are non-blocking
    }
  }

  /**
   * Helper to check if a value has actually changed (deep equality)
   */
  hasValueChanged(oldValue: any, newValue: any): boolean {
    // Handle null/undefined cases
    if (oldValue === newValue) return false;
    if (oldValue === null || oldValue === undefined) return newValue !== null && newValue !== undefined;
    if (newValue === null || newValue === undefined) return true;

    // Handle objects and arrays
    if (typeof oldValue === 'object' && typeof newValue === 'object') {
      return JSON.stringify(oldValue) !== JSON.stringify(newValue);
    }

    // Handle primitives
    return oldValue !== newValue;
  }
}

export const fieldTriggerService = new FieldTriggerService();
